import { IndustryDetailPage } from "@/components/pages/IndustryDetailPage";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowRight,
  Phone,
  Check,
  Sparkles,
  Target,
  Zap,
  BadgeCheck,
  ChevronRight,
} from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import {
  getIndustryBySlug,
  INDUSTRY_SLUGS,
  type IndustrySolution,
} from "@/data/industry-solutions";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/industries/$slug/")({
  loader: ({ params }) => {
    const ind = getIndustryBySlug(params.slug);
    if (!ind) throw notFound();
    return { ind };
  },
  head: ({ loaderData }) => {
    const ind = loaderData?.ind;
    if (!ind)
      return { meta: [{ title: ctaTitle("Industry — NYC Legal", "/industries/$slug", "th") }] };
    const url = `${SITE}/industries/${ind.slug}`;
    const title = `${ind.name} — Compliance · Translation · Visa Pack | NYC Language Institute`;
    const desc = `${ind.heroIntro.slice(0, 158)} — Ask us before you file — it's free.`;
    return {
      meta: [
        { title: ctaTitle(title, "/industries/$slug", "th") },
        { name: "description", content: ctaDesc(desc, "/industries/$slug", "th") },
        { name: "keywords", content: ind.keywords },
        { property: "og:title", content: ctaTitle(title, "/industries/$slug", "th") },
        { property: "og:description", content: ctaDesc(desc, "/industries/$slug", "th") },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "th_TH" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/industries/${ind.slug}`)],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${ind.name} Legal & Translation Solutions`,
              description: desc,
              url,
              provider: { "@id": `${SITE}#organization` },
              areaServed: { "@type": "Country", name: "Thailand" },
              audience: { "@type": "BusinessAudience", audienceType: ind.audience },
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: `${ind.name} Packages`,
                itemListElement: ind.packages.map((p, i) => ({
                  "@type": "Offer",
                  position: i + 1,
                  name: p.name,
                  price: p.price.replace(/[^\d]/g, "") || undefined,
                  priceCurrency: "THB",
                  description: p.includes.join(", "),
                })),
              },
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: ind.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Industry Hub",
                  item: `${SITE}/industries`,
                },
                { "@type": "ListItem", position: 3, name: ind.name, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: IndustryDetailPage,
  errorComponent: ({ error }) => (
    <main className="grid min-h-screen place-items-center bg-navy-deep p-6 text-white">
      <div className="text-center">
        <h1 className="text-2xl font-bold">เกิดข้อผิดพลาด</h1>
        <p className="mt-2 text-white/70">{(error as Error)?.message}</p>
        <Link
          to="/industries"
          className="mt-4 inline-block rounded-lg bg-amber-500 px-4 py-2 font-semibold text-navy-deep"
        >
          กลับ Industry Hub
        </Link>
      </div>
    </main>
  ),
  notFoundComponent: () => (
    <main className="grid min-h-screen place-items-center bg-navy-deep p-6 text-white">
      <div className="text-center">
        <h1 className="text-3xl font-extrabold">ไม่พบ Industry</h1>
        <p className="mt-2 text-white/70">โปรดเลือกจาก Industry Hub</p>
        <Link
          to="/industries"
          className="mt-4 inline-block rounded-lg bg-amber-500 px-4 py-2 font-semibold text-navy-deep"
        >
          กลับ Industry Hub
        </Link>
      </div>
    </main>
  ),
});
